/**
 * useSystemVersion — Issue #339
 *
 * Loads the deployment release metadata that backs the 版本信息 dialog beside
 * the 退出登录 button. Backed by `GET /api/v1/system/version` (the typed
 * api-client). The hook never invents version data:
 *   - On success it surfaces whatever the backend returned, including the
 *     `source` discriminator ("manifest" / "fallback" / "unavailable") so the
 *     UI can flag non-production payloads.
 *   - On failure (network/HTTP) it sets a translated message and leaves
 *     `data` null — the dialog renders a 加载失败 state, not stale guesses.
 */

import { useCallback, useState } from 'react';
import {
  ApiError,
  createApiClient,
  type SystemVersionDto,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
  fetch: (...args: Parameters<typeof globalThis.fetch>) => globalThis.fetch(...args),
});

export interface SystemVersionApiError {
  readonly message: string;
}

export interface UseSystemVersionResult {
  readonly data: SystemVersionDto | null;
  readonly loading: boolean;
  readonly error: SystemVersionApiError | null;
  readonly fetch: () => Promise<void>;
}

function describeFailure(err: unknown, httpStatus?: number): SystemVersionApiError {
  const status =
    httpStatus ?? (err instanceof ApiError ? err.status : undefined);
  if (status === 401) return { message: '未登录或会话已过期，无法读取版本信息' };
  if (status === 403) return { message: '当前账号没有读取版本信息的权限' };
  if (typeof status === 'number' && status >= 500) {
    return { message: '后端读取版本信息失败，请稍后重试' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: '读取版本信息失败' };
}

export function useSystemVersion(): UseSystemVersionResult {
  const [data, setData] = useState<SystemVersionDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<SystemVersionApiError | null>(null);

  const fetch = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const { data: body, error: apiErr, response } = await apiClient.GET(
        '/api/v1/system/version',
        {},
      );
      if (apiErr) {
        setError(describeFailure(apiErr, (response as Response | undefined)?.status));
        setData(null);
        return;
      }
      if (body) {
        setData(body);
      } else {
        setError(describeFailure(new Error('empty response')));
      }
    } catch (err) {
      setError(describeFailure(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetch };
}
